import { useEffect } from 'react'
import { Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'

import AutoCompleteField from 'src/components/shared/ui/form/fields/AutoCompleteField'
import RequiredText from 'src/components/views/EscritosLexnet/components/shared/components/RequiredText'
import { OrganoOrigenFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/organoOrigenTypes'
import { FormField } from 'src/enums/Form'
import {
  useGetComunidades,
  useGetOrganosJudiciales,
  useGetPartidosJudiciales,
  useGetProvincias
} from 'src/hooks/fetch/useCatalogoGeneralFilters'
import { OrganoJudicial } from 'src/interfaces/Catalog'

function OrigenForm(): JSX.Element {
  const { values, setFieldValue } = useFormikContext<OrganoOrigenFormValues>()

  const comunidades = useGetComunidades(FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA)
  const provincias = useGetProvincias(
    FormField.ORGANO_ORIGEN_PROVINCIA,
    values[FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA]
  )
  const partidosJudiciales = useGetPartidosJudiciales(
    FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL,
    values[FormField.ORGANO_ORIGEN_PROVINCIA]
  )
  const organosJudiciales = useGetOrganosJudiciales(FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL, [
    values[FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL],
    null
  ])

  function handleFieldChange(field: string) {
    if (field === FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA) {
      setFieldValue(FormField.ORGANO_ORIGEN_PROVINCIA, null, false)
      setFieldValue(FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL, null, false)
    }

    if (field === FormField.ORGANO_ORIGEN_PROVINCIA) {
      setFieldValue(FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL, null, false)
    }

    if (field === FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL) {
      setFieldValue(FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL, null, false)
    }

    if (field === FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL) {
      setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL, null, true)
    }
  }

  useEffect(() => {
    const organo: OrganoJudicial | null = values[FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL]
    if (organo != null) {
      if (organo.ordenes && organo.ordenes.length === 1) {
        setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL, organo.ordenes[0], false)
      }
    } else {
      setFieldValue(FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL, null, false)
    }
    // eslint-disable-next-line
  }, [values[FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL]])

  return (
    <>
      <RequiredText />
      <Grid container spacing={2} justifyContent={'center'}>
        <Grid item xs={7}>
          <AutoCompleteField
            id={FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA}
            items={comunidades}
            onChange={handleFieldChange}
            label={t('form.field.comunidad')}
            itemName="descripcion"
            required
          />
        </Grid>
        <Grid
          item
          xs={7}
          display={values[FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA] ? 'unset' : 'none'}
        >
          <AutoCompleteField
            id={FormField.ORGANO_ORIGEN_PROVINCIA}
            items={provincias}
            onChange={handleFieldChange}
            label={t('form.field.provincia')}
            itemName="descripcion"
            disabled={values[FormField.ORGANO_ORIGEN_COMUNIDAD_AUTONOMA] === null}
            required
          />
        </Grid>
        <Grid item xs={7} display={values[FormField.ORGANO_ORIGEN_PROVINCIA] ? 'unset' : 'none'}>
          <AutoCompleteField
            id={FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL}
            items={partidosJudiciales}
            onChange={handleFieldChange}
            label={t('form.field.partido_judicial')}
            itemName="nombrePartido"
            disabled={values[FormField.ORGANO_ORIGEN_PROVINCIA] === null}
            required
          />
        </Grid>
        <Grid item xs={7} display={values[FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL] ? 'unset' : 'none'}>
          <AutoCompleteField
            id={FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL}
            items={organosJudiciales}
            onChange={handleFieldChange}
            label={t('form.field.organo_judicial')}
            itemName="nombre"
            itemKey="id"
            disabled={values[FormField.ORGANO_ORIGEN_PARTIDO_JUDICIAL] === null}
            required
          />
        </Grid>
        <Grid
          item
          xs={7}
          display={values[FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL] ? 'unset' : 'none'}
        >
          <AutoCompleteField
            id={FormField.ORGANO_ORIGEN_ORDEN_JUDICIAL}
            items={values[FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL]?.ordenes || []}
            onChange={handleFieldChange}
            label={t('form.field.orden')}
            itemName="descripcion"
            disabled={values[FormField.ORGANO_ORIGEN_ORGANO_JUDICIAL] === null}
            required
          />
        </Grid>
      </Grid>
    </>
  )
}

export default OrigenForm
